import { selectiveCdt1 } from "./selectiveCdt1";
import { planCompression, type CompressionStrategy } from "./compressionPlan";
/** Preserve tree groups, split a cdt1 child into its own column only where the columns stay
 * real vertical blocks, and retain deeper attributes. */
import { buildLayout } from "./buildLayout";
import { layoutPlacementsOf } from "./shelfRankSpace";
import {
  VERTICAL_TIERS,
  bandWidthCmOf,
  type CdtLevelPlan,
  columnsAfford,
  type LevelRole,
} from "./cdtLevelPlan";
import {
  VERTICAL_SCORE,
  measureAcrossOf,
  measureBlockingOf,
  measureShelfRowsOf,
  rootBlockingScopes,
  type AcrossReading,
  type BlockingScope,
} from "./blockOrientation";
import {
  FORM_ATTR,
  SIZE_TIER_ATTR,
  SIZE_TIER_ORDER,
  SUB_BRAND_ATTR,
  type SizeLayout,
  type WithinOrder,
  sizeLayoutOf,
  sizeTierRank,
  subBrandOf,
  withinOrderOf,
} from "./sizeTiers";
import { type RowLine, replicateRows, rowLineId, stackedBySize } from "./rowLines";
import {
  NEAR_UNIQUE_RATIO,
  annotateContract,
  measureOrientation,
  runOffsetsOf,
} from "./spineInference";
import {
  CollapsedContract,
  CollapsedLevel,
  GeneratedLayout,
  LayoutDiff,
  Orientation,
  PATH_SEPARATOR,
  SpineLevel,
  StudioDraft,
  StudioItem,
  StudioPlacement,
  StudioRunGeometry,
  effectiveAttrs,
  isPlaceholderValue,
  wholeInches,
} from "./studioTypes";

// Colon, not hyphen: item-master values already contain hyphens.
export const COMPOUND_JOIN = " : ";
const CDT1_LEVEL = 1;

/** Strip badges before NFKD so ™ does not become the letters TM. */
export const sanitizeCdtValue = (value: string | null | undefined): string =>
  String(value ?? "")
    .replace(/[®™©]/g, "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”‟]/g, '"')
    .replace(/×/g, "x")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/[:,"\r\n\t]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const splitCompoundValue = (value: string): string[] => value.split(COMPOUND_JOIN);

export interface LevelGroup {
  levels: SpineLevel[];
  orient: Orientation;
  role: LevelRole;
}

export const collapseGroups = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[] = [],
  strategy: CompressionStrategy = {},
): LevelGroup[] =>
  planCompression(items, draft, runs, strategy).levels.map((level) => ({
    levels: level.levels,
    orient: level.orient,
    role: level.role,
  }));

/** Tier attributes per cdt0 branch (keyed by branch, never per item, or two siblings could put
 * different attributes on one tier). A tier the branch cannot fill is planned empty rather
 * than pulling a deeper attribute that makes shelf rows up into a vertical tier; a product left
 * blank there is lifted on its own by `skipBlankTiers`. */
const branchTierAttrs = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  plan: CdtLevelPlan,
): ((attrs: Record<string, string | null>) => readonly (readonly string[])[]) => {
  const planAttrs = plan.levels.map((level) => level.levels.map((source) => source.attr));
  const branchAttr = plan.branchAttr ?? plan.levels[0]?.levels[0]?.attr;
  if (!branchAttr) return () => planAttrs;
  const byBranch = new Map(plan.branches.map((branch) => [branch.value, branch.tiers]));
  return (attrs) => byBranch.get(String(attrs[branchAttr] ?? "")) ?? planAttrs;
};

/** A blank level is not a step on that product's path, at any tier: the backend tree drops a
 *  blank part with every part beneath it (cdt2 = "" cut a filled cdt3 off to a brand-only
 *  chain) and refuses a blank cdt1 outright. Per product, cdt0 (the section) stays; below it
 *  the planned tiers are walked in order, blank ones (and ones the branch left empty) skipped,
 *  and the levels below move up: each tier takes the next level the product fills (a
 *  compound tier moves whole), the last tier keeps the rest. A product that fills nothing
 *  below cdt0 stays a shallow chain. One exception keeps the vertical contract: the vertical
 *  cdt1 passes over the branch's shelf-row level (its planned cdt2) and takes the next filled
 *  level instead, so a brandless product's form stays a shelf row under a vertical block rather
 *  than standing as a full-height vertical block of its own; the shelf-row level then fills
 *  cdt2. Only when the shelf-row level is all the product fills does it stand at cdt1, because
 *  a blank cdt1 is refused outright.
 *  One parent may hold children from different levels (an Eco SKU's "Eco / Plant-based"
 *  beside its siblings' forms); `contract.levels` keeps naming the planned levels. */
const skipBlankTiers = (
  attrs: Record<string, string | null>,
  tierAttrs: readonly (readonly string[])[],
  tierCount: number,
): readonly (readonly string[])[] => {
  const last = tierCount - 1;
  const blank = (unit: readonly string[]) =>
    unit.every((attr) => sanitizeCdtValue(attrs[attr]) === "");
  const upper = Array.from({ length: Math.max(0, last - 1) }, (_, i) => tierAttrs[i + 1] ?? []);
  if (!upper.some(blank)) return tierAttrs;
  // Tiers above the last move whole; the last tier's attributes move one at a time.
  const units = [...upper, ...(tierAttrs[last] ?? []).map((attr) => [attr])];
  // The branch's shelf-row level: units[VERTICAL_TIERS - 1], when a tier below it is left to lift.
  const rowTier = VERTICAL_TIERS < last ? VERTICAL_TIERS - 1 : -1;
  const open = units.map((unit) => !blank(unit));
  const take = (eligible: (index: number) => boolean): readonly string[] => {
    const index = units.findIndex((_, i) => open[i] && eligible(i));
    if (index < 0) return [];
    // Passing over the shelf-row level leaves it open for cdt2; every other level above is spent.
    for (let i = 0; i <= index; i += 1) if (i !== rowTier || index === rowTier) open[i] = false;
    return units[index];
  };
  const tiers: (readonly string[])[] = [tierAttrs[0] ?? []];
  for (let tier = 1; tier < last; tier += 1) {
    const unit = tier < VERTICAL_TIERS ? take((i) => i !== rowTier) : [];
    tiers.push(unit.length > 0 ? unit : take(() => true));
  }
  const first = open.indexOf(true);
  tiers.push(first < 0 ? [] : units.slice(first).flat());
  return tiers;
};

/** A cdt1 child joined onto its parent: the child's value and the attribute it came from. */
interface WideJoin {
  value: string;
  attr: string;
}

/** A product whose cdt1 took a joined child no longer needs the child's level: it is not a
 *  step on that product's path, so the attribute leaves every tier below cdt1 and
 *  `skipBlankTiers` lifts the levels below exactly as it does for a blank one ("Tide :
 *  Unit dose" is followed by the flavour, not by "Unit dose" again). */
const withoutJoinedLevel = (
  tierAttrs: readonly (readonly string[])[],
  attr: string,
): readonly (readonly string[])[] =>
  tierAttrs.map((unit, tier) => (tier <= CDT1_LEVEL ? unit : unit.filter((a) => a !== attr)));

/** The wide cdt1 children `selectiveCdt1` found, gated by width. A child joins cdt1 as its
 *  own vertical block ("Tide : Pods") only while every vertical block of the parent still
 *  affords two facings of its median pack. Past that the split carves one-SKU slivers — tree
 *  106 shipped one vertical block per SKU — so the child keeps cdt2 and the width is reported.
 *  Each join names the attribute that produced it (the branch's planned cdt2), so the collapse
 *  can take that level off the product's path below cdt1. */
/** Values that share shelves stand apart on every one of them, in one left-to-right order. */
const standsSideBySide = (across: AcrossReading): boolean =>
  across.mixed > 0 && across.exclusivity === 1 && across.order === 1;

/** Every value stands beside another value on at least one shelf, run by run. */
const eachMeetsAnother = (items: readonly StudioItem[], attr: string): boolean => {
  const valuesOn = new Map<string, Set<string>>();
  const shelvesOf = new Map<string, Set<string>>();
  for (const item of items) {
    const value = String(item.attrs[attr] ?? "");
    if (isPlaceholderValue(value)) continue;
    for (const { run, shelf } of item.placements) {
      const key = `${run}/${shelf}`;
      valuesOn.set(key, (valuesOn.get(key) ?? new Set<string>()).add(value));
      shelvesOf.set(value, (shelvesOf.get(value) ?? new Set<string>()).add(key));
    }
  }
  return [...shelvesOf.values()].every((shelves) =>
    [...shelves].some((shelf) => (valuesOn.get(shelf)?.size ?? 0) >= 2),
  );
};

/** Whether the base says a brand's children do NOT stand as vertical blocks of the brand: stacked
 *  in its shelf rows or mixed along its shelves rather than side by side. Studio's own test for a
 *  level that stands as columns (`measureBlockingOf` against `VERTICAL_SCORE`, the reading that
 *  decides vertical against horizontal blocking), applied to the brand's products alone. A brand
 *  whose children each stand on one shelf gives no evidence either way. `blocksStack`: the base
 *  stacks the brand blocks of the brand's cdt0 as shelf rows. */
const childrenNotColumns = (
  group: readonly StudioItem[],
  childAttr: string,
  runs: readonly StudioRunGeometry[],
  blocksStack: boolean,
): boolean => {
  const scopes = rootBlockingScopes(group, runs);
  const across = measureAcrossOf(group, childAttr, scopes);
  const oneRunEach = measureShelfRowsOf(group, childAttr, () => undefined, scopes).oneRun === 1;
  // Children that each stand beside another, in one left-to-right order wherever they meet, each
  // on one run of shelves, are vertical blocks, of one height where the seam moves along the
  // shelf (Viva Signature Cloth on base 51) or of two (Sparkle Tear-a-Square: Double beside
  // Triple on shelf 1, Double alone below). One child on a shelf of its own is a shelf row.
  if (oneRunEach && standsSideBySide(across) && eachMeetsAnother(group, childAttr)) return false;
  // Where the brand blocks stack as shelf rows, children on shelf rows of their own split into
  // brand blocks that stack as the base stands them (facial tissue release 79's Lotion 3-ply).
  if (blocksStack && oneRunEach && across.mixed === 0) return false;
  const reading = measureBlockingOf(group, childAttr, runs);
  // Stability compares a value with itself shelf to shelf: with no value on two shelves there is
  // nothing to compare.
  const readable = reading.values.some((value) => value.rows > 1);
  return readable && reading.score < VERTICAL_SCORE;
};

const gateWideChildren = (
  plan: CdtLevelPlan,
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  wideChildren: ReadonlyMap<string, string | null>,
): { joined: Map<string, WideJoin>; notes: string[] } => {
  const joined = new Map<string, WideJoin>();
  const notes: string[] = [];
  if (wideChildren.size === 0) return { joined, notes };
  const rootAttr = plan.levels[0]?.levels[0]?.attr;
  if (!rootAttr) return { joined, notes };
  const tiersOf = new Map(plan.branches.map((branch) => [branch.value, branch.tiers]));
  const groups = new Map<string, StudioItem[]>();
  for (const item of items) {
    if (!wideChildren.has(item.skuId)) continue;
    const attrs = effectiveAttrs(item, draft);
    const branch = String(attrs[rootAttr] ?? "");
    const parentAttr = tiersOf.get(branch)?.[1]?.[0];
    if (!parentAttr) continue;
    const key = [branch, String(attrs[parentAttr] ?? "")].join(PATH_SEPARATOR);
    const group = groups.get(key) ?? [];
    group.push({ ...item, attrs });
    groups.set(key, group);
  }
  /** The base stacks this cdt0's brand blocks as shelf rows: Studio's own reading of a level's
   *  direction (`measureOrientation`, which inference records in `orientOverrides`). */
  const brandBlocksStack = (branch: string, parentAttr: string): boolean => {
    const blocks = new Map<string, StudioItem[]>();
    for (const item of items) {
      const attrs = effectiveAttrs(item, draft);
      if (String(attrs[rootAttr] ?? "") !== branch) continue;
      const block = String(attrs[parentAttr] ?? "");
      if (isPlaceholderValue(block)) continue;
      blocks.set(block, [...(blocks.get(block) ?? []), item]);
    }
    return (
      blocks.size >= 2 &&
      measureOrientation([...blocks.values()], runOffsetsOf(runs)).orientation === "vertical"
    );
  };
  for (const [key, group] of groups) {
    const [branch, parent] = key.split(PATH_SEPARATOR);
    const tiers = tiersOf.get(branch);
    const parentAttr = tiers?.[1]?.[0] ?? "";
    const childAttr = tiers?.[2]?.[0] ?? "";
    // A child is a vertical block only where the children stand side by side on the base. Tide
    // Evo's one Free & Gentle product stands on shelf 5 among its tiles, and ARM & HAMMER's pods
    // fill the top shelf over its liquids: wide enough, but shelf rows of the brand. Split, the
    // store stacked them as separate blocks (store planogram 10823).
    const affords = columnsAfford(group, [parentAttr, childAttr], bandWidthCmOf(group, runs));
    if (
      affords &&
      childrenNotColumns(group, childAttr, runs, brandBlocksStack(branch, parentAttr))
    ) {
      notes.push(
        `In "${branch}", "${parent}" does not stand its ${childAttr} values side by side on the base (they stack in shelf rows or mix along the shelf), so ${childAttr} stays below cdt1 inside the "${parent}" brand block.`,
      );
      continue;
    }
    if (affords) {
      for (const item of group) {
        const child = wideChildren.get(item.skuId);
        if (child != null) joined.set(item.skuId, { value: child, attr: childAttr });
      }
      continue;
    }
    const children = [
      ...new Set(
        group.flatMap((item) => {
          const child = wideChildren.get(item.skuId);
          return child == null ? [] : [`"${sanitizeCdtValue(child)}"`];
        }),
      ),
    ];
    notes.push(
      `In "${branch}", "${parent}" has a ${childAttr} wide enough to stand as its own vertical block (${children.join(", ")}), but splitting "${parent}" by ${childAttr} would leave vertical blocks thinner than two facings; ${childAttr} stays below cdt1 inside the "${parent}" brand block instead.`,
    );
  }
  return { joined, notes };
};

/** One step below cdt1 on a product's path: a planned unit (a compound tier moves whole, the
 *  tail's attributes one at a time) and the product's value there ("" = blank). */
interface PathUnit {
  key: string;
  value: string;
}

/** A product's path after the cdt1 join and the blank skip, before the informative pass. */
interface PathDraft {
  skuId: string;
  /** cdt0 › cdt1, the parent every decision below is scoped to. */
  head: string;
  units: PathUnit[];
}

const unitsBelowCdt1 = (
  attrs: Record<string, string | null>,
  tierAttrs: readonly (readonly string[])[],
): PathUnit[] =>
  [tierAttrs[2] ?? [], ...(tierAttrs[3] ?? []).map((attr) => [attr])]
    .filter((unit) => unit.length > 0)
    .map((unit) => {
      const parts = unit.map((attr) => sanitizeCdtValue(attrs[attr]));
      return {
        key: unit.join("+"),
        value: parts.every((part) => part === "") ? "" : parts.join(COMPOUND_JOIN),
      };
    });

/** A product inside one scope: its path and the unit keys it has already spent above. */
interface Member {
  path: PathDraft;
  /** The product's units not yet spent above, in path order, and their values by key. */
  open: readonly PathUnit[];
  values: ReadonlyMap<string, string>;
}

const memberOf = (path: PathDraft, spent: ReadonlySet<string> = new Set()): Member => {
  const open = path.units.filter((unit) => !spent.has(unit.key));
  const values = new Map<string, string>();
  for (const unit of open) if (!values.has(unit.key)) values.set(unit.key, unit.value);
  return { path, open, values };
};

const openUnits = (member: Member): readonly PathUnit[] => member.open;

const valueAt = (member: Member, key: string): string => member.values.get(key) ?? "";

/** Unit keys in the order the members carry them (walk-sorted members, first appearance). */
const keyOrder = (members: readonly Member[]): string[] => {
  const keys = new Set<string>();
  for (const member of members) for (const unit of openUnits(member)) keys.add(unit.key);
  return [...keys];
};

/** Filled values of `key` among the members, with how many members carry each. */
const groupsAt = (members: readonly Member[], key: string): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const member of members) {
    const value = valueAt(member, key);
    if (value !== "") counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return counts;
};

/** How many ways `key` splits the members: each filled value, plus the products that leave it
 *  blank when some fill it ("Eco" beside unbranded siblings is a split; "Hard" on all is not). */
const splitsInto = (members: readonly Member[], key: string): number => {
  const groups = groupsAt(members, key);
  const filled = [...groups.values()].reduce((sum, count) => sum + count, 0);
  return groups.size + (groups.size > 0 && filled < members.length ? 1 : 0);
};

/** Share of the products filling `key` that share their value with another product in the
 *  same scope. A level unique to each product groups nothing, whatever its geometry says. */
const groupedShare = (scopes: readonly (readonly Member[])[], key: string): number => {
  let grouped = 0;
  let filled = 0;
  for (const members of scopes)
    for (const count of groupsAt(members, key).values()) {
      filled += count;
      if (count > 1) grouped += count;
    }
  return filled > 0 ? grouped / filled : 0;
};

/** Products a level's values must hold on average before it groups products at all. A level
 *  with fewer (a pack size, a code) labels products nearly one by one: it takes a tier only as
 *  a last resort (`LAST_RESORT`). */
const GROUP_SIZE = 2;

/** How a level groups the products it is read over, from its value count against the
 *  product count:
 *  - "groups": no more values than `NEAR_UNIQUE_RATIO` of the products (inference's own line
 *    between a level and a near-unique one);
 *  - "pairs": its values still hold `GROUP_SIZE` products on average;
 *  - "unique": fewer than that.
 *  A tier takes a "groups" level when any qualifies, else a "pairs" level; a "unique" one only
 *  as a last resort. */
export type LevelGrouping = "groups" | "pairs" | "unique";

const groupingOf = (values: number, products: number): LevelGrouping => {
  const ratio = products > 0 ? values / products : 1;
  if (ratio <= NEAR_UNIQUE_RATIO) return "groups";
  return ratio <= 1 / GROUP_SIZE ? "pairs" : "unique";
};

export interface LevelGroups {
  /** Distinct filled values. */
  values: number;
  grouping: LevelGrouping;
}

const levelGroupsOf = (members: readonly Member[], key: string): LevelGroups => {
  const groups = groupsAt(members, key);
  const filled = [...groups.values()].reduce((sum, count) => sum + count, 0);
  return { values: groups.size, grouping: groupingOf(groups.size, filled) };
};

/** What the base pog says about one brand block (a cdt0 › cdt1 node): how
 *  strongly each remaining level stacks it into shelf rows (cdt2) and, inside the chosen
 *  rows, how strongly each level stands side by side along the shelf (cdt3). Each strength
 *  is the base measurement times `groupedShare`. */
export interface BrandBlockTierReading {
  column: string;
  skus: number;
  /** Shelves the brand block stands on (run x shelf). */
  shelves: number;
  /** Level -> how the base reads it across the whole brand block, both ways:
   *  rows = shelf rows top to bottom: one run of shelves per value, in the level's order, edges
   *  may be shared (`measureShelfRowsOf`);
   *  across = side by side per shelf, one order, lined up (`measureAcrossOf`);
   *  each times the grouped share. cdt2 is chosen on `rows`. */
  cdt2Scores: Readonly<Record<string, TierScore>>;
  /** The same two readings inside the cdt2 rows. cdt3 is chosen on `across`. */
  cdt3Scores: Readonly<Record<string, TierScore>>;
  /** Level -> how it groups the brand block's products (cdt2) and the rows' products (cdt3). */
  cdt2Groups: Readonly<Record<string, LevelGroups>>;
  cdt3Groups: Readonly<Record<string, LevelGroups>>;
  /** Null: no level reads as rows, nor along the brand block as one row; cdt2 and cdt3 ship
   *  blank. */
  cdt2: TierChoice | null;
  /** Null: no level groups the rows' products along the shelf; cdt3 ships blank. */
  cdt3: TierChoice | null;
  /** cdt2 values in the base's order: left to right where cdt2 stands side by side
   *  (a sub-brand, a form, a size tier), else Small to Family for the size tier, else top to bottom
   *  as the base stacks them. */
  rowOrder: readonly string[];
  /** What the pass decided that a reader should know: a contradicted size tier, a product
   *  placed in a form it does not name. */
  notes: readonly string[];
  /** Stacked rows only: whether sorting the block by its rows gives the base back
   *  (`replicateRows`), the lines that explain the base where it does not, and whether anything
   *  is left that no line explains. */
  replicates?: boolean;
  lines?: readonly RowLine[];
  unexplained?: boolean;
  /** Brand > [Form >] Size tier > Flavour only: the size rows that may share a shelf on a store
   *  (`SizeTierGroups`). Absent for every other brand block. */
  sizeTiers?: SizeTierGroups;
  /** Brand > [Form >] Size tier > Flavour only: the base shows neither layout (`sizeLayoutOf`
   *  reads it unclear), so Studio asks the merchant (`sizeLayoutFlags`): its sub-brands top to
   *  bottom as the base stands them, and whether its pack sizes run small to large. The block
   *  ships the answer (`StudioDraft.sizeLayoutAnswers`), never a layout Studio chose. */
  sizeLayoutQuestion?: { subBrands: readonly string[]; sizesRunUp: boolean };
}

/** A brand block whose sizes the base stacks as rows: each group is consecutive size rows of one
 *  form (or the block's own size rows), top to bottom; the store's shelf count decides which of
 *  them share a shelf. `cdt3Order` is the one cdt3 (flavour) order for the whole brand block, left
 *  to right as each first appears on the base: a flavour takes the same priority in every row.
 *  `within` is the order along a shelf where two or more of its sizes share it (`withinOrderOf`).
 *  `layout` is how the base lays the block out (`sizeLayoutOf`): its size tiers as shelf rows with
 *  the sub-brands side by side, or two or more sub-brands each on rows of their own, sizes left
 *  to right; a horizontal block ships as sub-brand rows (`subBrandRowsOf`). */
export interface SizeTierGroups {
  groups: readonly (readonly string[])[];
  cdt3Order: readonly string[];
  within: WithinOrder;
  layout: SizeLayout;
}

export interface TierScore {
  rows: number;
  across: number;
}

export interface TierChoice {
  attr: string;
  /** "form": the block mixes forms; "sub-brand": the base lays the brand block out by
   *  sub-brand (`subBrandLayoutOf`); "size tier": the base does not contradict it; "base":
   *  the level the base reads best; "quantity": no level that groups products reads as rows,
   *  the profile's quantity level does; "one row": no level reads as rows, the brand block
   *  stands as one row named by a level every product shares. */
  source: "base" | "sub-brand" | "size tier" | "form" | "quantity" | "one row";
  /** "vertical" = the values stack as rows; "horizontal" = they stand side by side. */
  orient: Orientation;
}

/** Whether the base leaves the size tier standing as the merchandising level: it does unless
 *  it contradicts it both ways. As shelf rows it holds when every tier stands on one run of shelves
 *  and the runs come Small to Family top to bottom (`measureShelfRowsOf`: order 1, one run each;
 *  rows may share their edge shelves). Side by side it holds when every pair of tiers is
 *  apart (exclusivity 1) in one left-to-right order (order 1) on every stretch they share. */
const sizeTierReadsAsRows = (
  items: readonly StudioItem[],
  scopes: readonly BlockingScope[],
): boolean => sizeTierLayoutOf(items, scopes) !== null;

/** Which way the size tier ships where it reads (`sizeTierReadsAsRows`): side by side only where
 *  no size stands higher than another (every pair of sizes centred on the same shelves:
 *  `measureShelfRowsOf` level 1) and the test that reads a sub-brand or a form side by side
 *  holds (`layoutOf`). Foca on base 210 stands Small, Medium and Large beside each other on one
 *  shelf. Anywhere one size stands above another (Tide's powders: Small beside Medium on top,
 *  Large on the two shelves below) it ships as rows, as before. Null: the base contradicts it. */
const sizeTierLayoutOf = (
  items: readonly StudioItem[],
  scopes: readonly BlockingScope[],
): Orientation | null => {
  const across = measureAcrossOf(items, SIZE_TIER_ATTR, scopes);
  const rows = measureShelfRowsOf(items, SIZE_TIER_ATTR, rankFor(SIZE_TIER_ATTR, {}), scopes);
  const acrossHolds = standsSideBySide(across);
  const shelfRowsHold = rows.order === 1 && rows.oneRun === 1;
  if (!acrossHolds && !shelfRowsHold) return null;
  const level = acrossHolds && rows.level === 1;
  return level && layoutOf(items, SIZE_TIER_ATTR, scopes) === "horizontal"
    ? "horizontal"
    : "vertical";
};

/** Whether the base lays these products out by sub-brand, and which way: each sub-brand one
 *  piece, apart from the others, in one order. One piece: every sub-brand stands on one run of
 *  shelves (`measureShelfRowsOf`: one run each), so Tide's pods, Original on shelves 1 and 3
 *  with Power PODS on 2 and 4-6 between and below, are not laid out by sub-brand. Side by side
 *  ("horizontal") when sub-brands share shelves and on every shelf they share stand apart in one
 *  left-to-right order (`measureAcrossOf`: mixed > 0, exclusivity 1, order 1; they need not
 *  line up exactly shelf to shelf). As shelf rows ("vertical") when each stands at its own
 *  height (order 1) and never overlaps another on a shelf they share. Null: the base mixes
 *  them, so the sub-brand does not lead. */
const layoutOf = (
  items: readonly StudioItem[],
  attr: string,
  scopes: readonly BlockingScope[],
): Orientation | null => {
  const rows = measureShelfRowsOf(items, attr, () => undefined, scopes);
  if (rows.oneRun !== 1 || rows.inside !== 1) return null;
  const across = measureAcrossOf(items, attr, scopes);
  if (standsSideBySide(across)) return "horizontal";
  const apart = across.mixed === 0 || across.exclusivity === 1;
  return rows.order === 1 && apart ? "vertical" : null;
};

const subBrandLayoutOf = (
  items: readonly StudioItem[],
  scopes: readonly BlockingScope[],
): Orientation | null => layoutOf(items, SUB_BRAND_ATTR, scopes);

/** A level whose value stands both above and below a shelf only its other values fill cannot
 *  split the brand block top to bottom. */
const interleavesRows = (attr: string, scopes: readonly BlockingScope[]): boolean =>
  scopes.some((scope) => {
    const rowsOf = new Map<string, Set<number>>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      const rows = rowsOf.get(value) ?? new Set<number>();
      rowsOf.set(value, rows);
      for (const placement of item.placements)
        if (placement.run === scope.run) rows.add(placement.shelf);
    }
    return [...rowsOf].some(([value, rows]) => {
      const bottom = Math.max(...rows);
      for (let row = Math.min(...rows) + 1; row < bottom; row += 1)
        if (
          !rows.has(row) &&
          [...rowsOf].some(([other, theirs]) => other !== value && theirs.has(row))
        )
          return true;
      return false;
    });
  });

/** A level's reading order top to bottom: Small to Family for the size tier, else the plan's
 *  value order for the attribute (values it does not list keep any order). */
const rankFor = (
  attr: string,
  valueOrder: Readonly<Record<string, readonly string[]>>,
): ((value: string) => number | undefined) => {
  if (attr === SIZE_TIER_ATTR)
    return (value) => {
      const rank = sizeTierRank(value);
      return rank < SIZE_TIER_ORDER.length ? rank : undefined;
    };
  const order = valueOrder[attr] ?? [];
  return (value) => {
    const index = order.indexOf(value);
    return index < 0 ? undefined : index;
  };
};

/** Both readings of each level over the given scopes. */
const readLevels = (
  keys: readonly string[],
  items: readonly StudioItem[],
  scopes: readonly BlockingScope[],
  members: readonly (readonly Member[])[],
  valueOrder: Readonly<Record<string, readonly string[]>>,
): Record<string, TierScore> => {
  const scores: Record<string, TierScore> = {};
  for (const key of keys) {
    const share = groupedShare(members, key);
    const rows = measureShelfRowsOf(items, key, rankFor(key, valueOrder), scopes);
    scores[key] = {
      rows: +(rows.score * share).toFixed(3),
      across: +(measureAcrossOf(items, key, scopes).score * share).toFixed(3),
    };
  }
  return scores;
};

const singleAttr = (key: string) => !key.includes("+");

/** One scope per run the members stand in, so a brand block spanning runs is measured in each. */
const scopesOf = (
  members: readonly Member[],
  itemOf: ReadonlyMap<string, StudioItem>,
  path: readonly string[],
): BlockingScope[] => {
  const byRun = new Map<number, StudioItem[]>();
  for (const { path: member } of members) {
    const item = itemOf.get(member.skuId);
    if (!item) continue;
    for (const run of new Set(item.placements.map((placement) => placement.run)))
      byRun.set(run, [...(byRun.get(run) ?? []), item]);
  }
  return [...byRun].map(([run, items]) => ({ run, path, items }));
};

const GROUPING: readonly LevelGrouping[] = ["groups", "pairs"];

/** Where nothing groups: a small brand block (Viva Signature Cloth : Double, 3 products, two
 *  pack sizes) splits only on levels that are "unique" by count. One still wins when some
 *  products share a value (its score is times the grouped share) and the base reads it. */
const LAST_RESORT: readonly LevelGrouping[] = ["unique"];

/** The level a tier takes: the best score among levels of the first of `gradings` that has
 *  one. Equal scores (three decimals) go to the level with more values (finer rows, finer
 *  groups), then to the shallower level. Null when no level scores above zero. */
const chooseLevel = (
  scores: Readonly<Record<string, number>>,
  groups: Readonly<Record<string, LevelGroups>>,
  order: readonly string[],
  gradings: readonly LevelGrouping[] = GROUPING,
): string | null => {
  for (const grouping of gradings) {
    let best: string | null = null;
    for (const attr of order) {
      const score = scores[attr];
      const group = groups[attr];
      if (score === undefined || !group || group.grouping !== grouping || !(score > 0)) continue;
      if (
        best === null ||
        score > scores[best] ||
        (score === scores[best] && group.values > groups[best].values)
      )
        best = attr;
    }
    if (best !== null) return best;
  }
  return null;
};

/** Groups in the order the base shows them: width-weighted mean of `position` over each
 *  group's placements, ties in the order the groups come (walk order). Size tiers read Small
 *  to Family. */
const orderedGroups = (
  groups: ReadonlyMap<string, readonly Member[]>,
  sizeTier: boolean,
  itemOf: ReadonlyMap<string, StudioItem>,
  position: (placement: StudioPlacement) => number,
): string[] => {
  const mean = new Map<string, number>();
  for (const [value, members] of groups) {
    let at = 0;
    let width = 0;
    for (const member of members)
      for (const placement of itemOf.get(member.path.skuId)?.placements ?? []) {
        at += position(placement) * placement.widthCm;
        width += placement.widthCm;
      }
    mean.set(value, width > 0 ? at / width : 0);
  }
  const first = [...groups.keys()];
  const key = (value: string) => (sizeTier ? sizeTierRank(value) : (mean.get(value) ?? 0));
  // Means equal but for float noise are a tie (a row read as 2.9999999999999996 is row 3),
  // whatever unit the plan's numbers are in.
  const TIE = 1e-9;
  return [...first].sort((a, b) => {
    const delta = key(a) - key(b);
    return Math.abs(delta) > TIE ? delta : first.indexOf(a) - first.indexOf(b);
  });
};

/** One order for a value across a brand block's rows, read off the base left to right row by
 *  row: a product stands where its leftmost placement stands (`position`, from the left of the
 *  planogram), a value stands in a row where the middle (median) of its products in that row
 *  stands, and in each row a value standing left of another comes before it there. It starts
 *  from each value's middle over all the rows (equal middles in walk order) and moves one value
 *  at a time to the place that puts fewer pairs out of order, summed over the rows, until no
 *  move does: it is never more out of order than that start. With one row (each cdt2 row of a
 *  brand block without size groups orders its own cdt3 this way) that is the row's own left to
 *  right order on the base. Over a brand block's size rows it is one flavour order for every
 *  row, so a flavour that leads its rows on the base leads the order even where another form of
 *  the brand stands further left (Downy's Infusions sheets at the left of shelf 1 do not put
 *  Infusions before April Fresh in the liquids). The values of the size rows (`sizeRows`) come
 *  first; a value with no product on the base after them, in the order it takes in the size row
 *  with the most products, then in walk order. Values only rows outside the size groups carry (a
 *  line row, a form not stacked by size) come last, the same way, except that size tiers there
 *  (`sizeTiers`, a row read by size, as the base stacks it) keep Small to Family, after the
 *  others. Rows' members come in walk order. */
const blockOrder = (
  rows: readonly { row: string; members: readonly Member[] }[],
  valueOf: (member: Member) => string,
  itemOf: ReadonlyMap<string, StudioItem>,
  position: (placement: StudioPlacement) => number,
  sizeRows: readonly string[],
  sizeTiers: ReadonlySet<string> = new Set(),
): string[] => {
  const median = (xs: readonly number[]) => {
    const sorted = [...xs].sort((a, b) => a - b);
    const half = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[half] : (sorted[half - 1] + sorted[half]) / 2;
  };
  const seen: string[] = [];
  /** Value -> where each of its products stands on the base. */
  const standing = new Map<string, number[]>();
  /** Row -> value -> where each of its products in that row stands on the base. */
  const inRow = new Map<string, Map<string, number[]>>();
  for (const { row, members } of rows)
    for (const member of members) {
      const value = valueOf(member);
      if (!seen.includes(value)) seen.push(value);
      const placements = itemOf.get(member.path.skuId)?.placements ?? [];
      if (placements.length === 0) continue;
      const at = Math.min(...placements.map(position));
      standing.set(value, [...(standing.get(value) ?? []), at]);
      const values = inRow.get(row) ?? new Map<string, number[]>();
      values.set(value, [...(values.get(value) ?? []), at]);
      inRow.set(row, values);
    }
  const middle = new Map([...standing].map(([value, at]) => [value, median(at)]));
  const rowMiddles = [...inRow.values()].map(
    (values) => new Map([...values].map(([value, at]) => [value, median(at)])),
  );
  /** How many rows stand `a` left of `b`. */
  const leftOf = (a: string, b: string) =>
    rowMiddles.filter((at) => at.has(a) && at.has(b) && at.get(a)! < at.get(b)!).length;
  const inSizeRows = rows.filter(({ row }) => sizeRows.includes(row));
  const largest = inSizeRows.reduce<(typeof rows)[number] | null>(
    (best, row) => (best === null || row.members.length > best.members.length ? row : best),
    null,
  );
  const inLargest = [...new Set((largest?.members ?? []).map(valueOf))];
  const ordered = (values: readonly string[]) => {
    const remaining = values
      .filter((value) => middle.has(value))
      .sort((a, b) => middle.get(a)! - middle.get(b)! || seen.indexOf(a) - seen.indexOf(b));
    const against = new Map(
      remaining.map((a) => [a, new Map(remaining.map((b) => [b, a === b ? 0 : leftOf(b, a)]))]),
    );
    /** Pairs out of order, summed over the rows: b placed after a where a row has b left. */
    const cost = (order: readonly string[]) =>
      order.reduce(
        (sum, a, i) =>
          sum + order.slice(i + 1).reduce((inner, b) => inner + against.get(a)!.get(b)!, 0),
        0,
      );
    let read = remaining;
    let best = cost(read);
    for (let moved = true; moved; ) {
      moved = false;
      for (let from = 0; from < read.length && !moved; from += 1)
        for (let to = 0; to < read.length && !moved; to += 1) {
          if (to === from) continue;
          const next = [...read];
          next.splice(to, 0, next.splice(from, 1)[0]);
          const nextCost = cost(next);
          if (nextCost < best) {
            read = next;
            best = nextCost;
            moved = true;
          }
        }
    }
    const unread = values.filter((value) => !middle.has(value));
    return [
      ...read,
      ...inLargest.filter((value) => unread.includes(value)),
      ...unread.filter((value) => !inLargest.includes(value)),
    ];
  };
  const sized = new Set(inSizeRows.flatMap(({ members }) => members.map(valueOf)));
  const rest = seen.filter((value) => !sized.has(value));
  return [
    ...ordered(seen.filter((value) => sized.has(value))),
    ...ordered(rest.filter((value) => !sizeTiers.has(value))),
    ...rest
      .filter((value) => sizeTiers.has(value))
      .sort((a, b) => sizeTierRank(a) - sizeTierRank(b) || seen.indexOf(a) - seen.indexOf(b)),
  ];
};

/** Tiers this pass ships, per product, what the base said about each brand block, and each
 *  read product's rank inside its brand block (cdt2 row top to bottom, then cdt3 group left to
 *  right). */
interface InformativeTiers {
  tiers: Map<string, [string, string]>;
  rank: Map<string, readonly [number, number]>;
  readings: BrandBlockTierReading[];
}

/** Every tier below cdt1 is one level per brand block, read brand block by brand block off the base
 *  pog. It runs only where Studio reads the base as vertically blocked and the plan is the
 *  tree's own (see `collapse4`).
 *  - Only levels that split the brand block may become a tier: a level constant in the brand block
 *    ("Hard" for all of Tide : Liquid) is skipped. Where the draft carries the profile's
 *    levels (`profileLevels`), only those may; the profile's ignored attributes never reach
 *    the spine at all.
 *  - cdt2: a brand block holding more than one form (`pack_format`) stacks by form, in the base's
 *    order. Otherwise the sub-brand, where the base lays the brand block out by it
 *    (`subBrandLayoutOf`), side by side left to right or as rows top to bottom as the base
 *    shows it. Otherwise the size tier, wherever the base does not contradict it
 *    (`sizeTierReadsAsRows`), as rows Small to Family. Otherwise the level whose values split the
 *    brand block's shelves into the cleanest rows (`measureShelfRowsOf`), among levels that group
 *    products (`chooseLevel`); a finer level wins only when it is equally clean. Where none
 *    does, the profile's quantity level if it reads as rows at all, else a near-unique level
 *    that does (`LAST_RESORT`). Else the brand block stands as one row (see `oneRow`).
 *  - cdt3: inside the cdt2 rows, the level that reads best side by side along the shelf
 *    (`measureAcrossOf`), among levels that group products (in one row, near-unique ones as a
 *    last resort); one level for the brand block. In a form-first brand block the sub-brand
 *    reads inside each form where the base lays the forms out by it, else the size tier where
 *    it splits the form. Under a sub-brand cdt2 the size tier reads inside each sub-brand where
 *    it splits it and the base does not contradict it.
 *  A product blank on the brand block's level is blank there (and below), and a brand block
 *  no level reads ships blank: no tier is ever two attributes joined. */
const informativeTiers = (
  paths: readonly PathDraft[],
  itemOf: ReadonlyMap<string, StudioItem>,
  runs: readonly StudioRunGeometry[],
  valueOrder: Readonly<Record<string, readonly string[]>> = {},
  /** The levels the category's profile declares (`StudioDraft.profileLevels`). Only these
   *  may become cdt2 or cdt3; any other attribute orders products through the walk. Absent:
   *  every level may. */
  profileLevels?: readonly string[],
  /** The profile's quantity level (`StudioDraft.quantityAttribute`). */
  quantityAttr?: string,
  /** The attributes a line row may be named by: the draft's levels. */
  lineAttrs: readonly string[] = [],
  /** Line row offers the merchant took, by flag id (`StudioDraft.appliedFixFlagIds`). */
  acceptedIds: ReadonlySet<string> = new Set(),
  /** The merchant's layout for each brand block the base lays out neither way
   *  (`StudioDraft.sizeLayoutAnswers`). Answered "horizontal", the block ships as sub-brand
   *  rows (`subBrandRowsOf`). */
  layoutAnswers: Readonly<Record<string, SizeLayout>> = {},
): InformativeTiers => {
  const declared = profileLevels ? new Set(profileLevels) : null;
  const levelAllowed = (key: string) =>
    !declared || key.split("+").every((attr) => declared.has(attr));
  const out = new Map<string, [string, string]>();
  const rank = new Map<string, readonly [number, number]>();
  const readings: BrandBlockTierReading[] = [];
  const offsets = runOffsetsOf(runs);
  const xOf = (placement: StudioPlacement) => (offsets[placement.run] ?? 0) + placement.xCm;
  const byHead = new Map<string, PathDraft[]>();
  for (const path of paths) byHead.set(path.head, [...(byHead.get(path.head) ?? []), path]);
  const itemsOf = (members: readonly Member[]) =>
    members.flatMap(({ path }) => itemOf.get(path.skuId) ?? []);

  for (const [head, paths] of byHead) {
    const column: Member[] = paths.map((path) => memberOf(path));
    const shelves = new Set(
      itemsOf(column).flatMap((item) => item.placements.map((p) => `${p.run}/${p.shelf}`)),
    ).size;
    // A brand block with no product on the base has nothing to read: it ships blank below cdt1.
    if (shelves === 0) {
      for (const path of paths) out.set(path.skuId, ["", ""]);
      readings.push({
        column: head,
        skus: paths.length,
        shelves,
        cdt2Scores: {},
        cdt3Scores: {},
        cdt2Groups: {},
        cdt3Groups: {},
        cdt2: null,
        cdt3: null,
        rowOrder: [],
        notes: ["no product of this brand block stands on the base, so nothing below cdt1 is read"],
      });
      continue;
    }
    const splitting = keyOrder(column).filter(
      (key) => levelAllowed(key) && splitsInto(column, key) >= 2,
    );
    const scored2 = splitting.filter(singleAttr);
    const cdt2Scores = readLevels(
      scored2,
      itemsOf(column),
      scopesOf(column, itemOf, [head]),
      [column],
      valueOrder,
    );
    const cdt2Groups = Object.fromEntries(scored2.map((key) => [key, levelGroupsOf(column, key)]));
    // A brand block holding more than one form stacks by form first (Great Value: Unit dose
    // over Liquid), in the base's order; the size tier then reads inside each form.
    const byForm = splitting.includes(FORM_ATTR);
    // Where the base lays the brand block out by sub-brand (ARM & HAMMER's pods: Deep Clean,
    // Clean Burst, plus OxiClean side by side), the sub-brand is the next level and takes cdt2 in
    // the base's order; the size tier reads inside each sub-brand.
    const subBrandOrient =
      !byForm && splitting.includes(SUB_BRAND_ATTR)
        ? subBrandLayoutOf(itemsOf(column), scopesOf(column, itemOf, [head]))
        : null;
    const bySubBrand = subBrandOrient !== null;
    // Otherwise the size tier is the merchandising level: it takes cdt2 whenever the base does
    // not contradict it, and ships as rows Small to Family, or side by side in the base's
    // left-to-right order where the base only stands the sizes beside each other.
    const tierOrient =
      !byForm && !bySubBrand && splitting.includes(SIZE_TIER_ATTR)
        ? sizeTierLayoutOf(itemsOf(column), scopesOf(column, itemOf, [head]))
        : null;
    const tierRead = tierOrient !== null;
    // A size tier that does not read takes no row, neither by score nor as a fallback. Only
    // two tagged tiers can contradict the base; one tagged tier beside untagged products is
    // one tier, which splits the brand block only by what the item master left blank.
    const unread = !byForm && !bySubBrand && splitting.includes(SIZE_TIER_ATTR) && !tierRead;
    const oneTier = groupsAt(column, SIZE_TIER_ATTR).size < 2;
    const notes: string[] = [];
    if (unread)
      notes.push(
        oneTier
          ? "only one size tier is tagged in this brand block, so it takes no shelf rows"
          : "the base contradicts the size tier here, so it takes no shelf rows",
      );
    // A level whose values interleave down the brand block takes no shelf rows, neither by
    // score, nor as the quantity, nor as a last resort.
    const rowScores = Object.fromEntries(
      Object.entries(cdt2Scores)
        .filter(
          ([key]) =>
            !(unread && key === SIZE_TIER_ATTR) &&
            !interleavesRows(key, scopesOf(column, itemOf, [head])),
        )
        .map(([key, score]) => [key, score.rows]),
    );
    const measured =
      byForm || bySubBrand || tierRead ? null : chooseLevel(rowScores, cdt2Groups, scored2);
    // The quantity level labels products one by one, so its grouped share is 0: it is read
    // as rows on the base reading alone.
    const quantity =
      !byForm &&
      !bySubBrand &&
      !tierRead &&
      measured === null &&
      quantityAttr !== undefined &&
      quantityAttr in rowScores &&
      measureShelfRowsOf(
        itemsOf(column),
        quantityAttr,
        rankFor(quantityAttr, valueOrder),
        scopesOf(column, itemOf, [head]),
      ).score > 0
        ? quantityAttr
        : null;
    // The forms make the rows, laid out the way the base lays them: side by side where each form
    // is one piece standing apart from the other in one left-to-right order on the shelves they
    // share (all's Mighty Pacs beside its liquids on every shelf of bay 8), the same test that
    // reads a sub-brand side by side (`layoutOf`); otherwise stacked.
    const formOrient: Orientation =
      byForm &&
      layoutOf(itemsOf(column), FORM_ATTR, scopesOf(column, itemOf, [head])) === "horizontal"
        ? "horizontal"
        : "vertical";
    const lastResort =
      byForm || bySubBrand || tierRead || measured !== null || quantity !== null
        ? null
        : chooseLevel(rowScores, cdt2Groups, scored2, LAST_RESORT);
    const rowChoice: TierChoice | null = byForm
      ? { attr: FORM_ATTR, source: "form", orient: formOrient }
      : subBrandOrient
        ? { attr: SUB_BRAND_ATTR, source: "sub-brand", orient: subBrandOrient }
        : tierRead
          ? { attr: SIZE_TIER_ATTR, source: "size tier", orient: tierOrient ?? "vertical" }
          : measured
            ? { attr: measured, source: "base", orient: "vertical" }
            : quantity
              ? { attr: quantity, source: "quantity", orient: "vertical" }
              : lastResort
                ? { attr: lastResort, source: "base", orient: "vertical" }
                : null;
    // No level reads as rows: the brand block stands as one row, named by a level every
    // product fills alike, and cdt3 is read along it. The name keeps that cdt3 (the backend
    // drops a blank cdt2 with everything beneath it); it ships only when cdt3 reads. A brand
    // block of full-height stripes (every product on every shelf it uses) is columns, not a
    // row, and still ships blank.
    const stripes =
      shelves > 1 &&
      itemsOf(column).every(
        (item) => new Set(item.placements.map((p) => `${p.run}/${p.shelf}`)).size === shelves,
      );
    const oneRow =
      rowChoice === null && !stripes
        ? (keyOrder(column).find(
            (key) =>
              singleAttr(key) &&
              levelAllowed(key) &&
              splitsInto(column, key) === 1 &&
              column.every((member) => valueAt(member, key) !== ""),
          ) ?? null)
        : null;
    const rowAttr = rowChoice?.attr ?? oneRow;

    // Do the rows give the base back? Where they do not, the lines that explain the base
    // (`replicateRows`); a line the merchant took (`acceptedIds`) stands as a row of its own,
    // at the top or bottom where the base stands it. One row has no rows to give back.
    // Only a level whose values group products (not one by one) names a line.
    const namingAttrs = lineAttrs.filter((attr) => {
      if (attr === SIZE_TIER_ATTR || attr === quantityAttr) return false;
      const values = itemsOf(column)
        .map((item) => String(item.attrs[attr] ?? ""))
        .filter((value) => value !== "");
      return groupingOf(new Set(values).size, values.length) !== "unique";
    });
    const replication =
      rowChoice?.orient === "vertical"
        ? replicateRows(
            itemsOf(column),
            rowChoice.attr,
            byForm || bySubBrand,
            namingAttrs,
            // A row the base stacks by one more attribute of the item master does not give the
            // base back, declared level or not (Gain's pods: Pack type "Soft" above "Hard" in
            // Small, Oxi "No oxi" above "Oxi" in Medium). Sub-brands stacked inside the size rows
            // are the block's size layout (`sizeLayoutOf`), read and asked there.
            namingAttrs.filter((attr) => attr !== SUB_BRAND_ATTR),
            declared,
          )
        : null;
    const takenLines = (replication?.lines ?? []).filter((line) =>
      acceptedIds.has(rowLineId(head, sanitizeCdtValue(line.name))),
    );
    const lineOf = new Map(
      takenLines.flatMap((line) =>
        line.skuIds.map((skuId) => [skuId, sanitizeCdtValue(line.name)] as const),
      ),
    );
    for (const line of replication?.lines ?? [])
      notes.push(
        `the rows do not give the base back; ${line.skuIds.length} ${line.value} products stand as the ${line.side} row: "${sanitizeCdtValue(line.name)}" is offered as a row of its own`,
      );
    if (replication?.unexplained)
      notes.push("the rows do not give the base back and no line explains it: a question");

    // #1: in a form-first brand block a product with no form takes the form the base shows on its
    // shelf (the most common form on that run's shelf in this brand block), else the brand
    // block's first form.
    const formOnShelf = (path: PathDraft): string | null => {
      // Shelf numbers repeat across runs: a neighbour shares the run as well as the shelf.
      const target = itemOf.get(path.skuId)?.placements[0];
      const counts = new Map<string, number>();
      for (const other of paths) {
        const form = other.units.find((unit) => unit.key === FORM_ATTR)?.value ?? "";
        if (form === "" || !target) continue;
        const placements = itemOf.get(other.skuId)?.placements ?? [];
        if (placements.some((p) => p.run === target.run && p.shelf === target.shelf))
          counts.set(form, (counts.get(form) ?? 0) + 1);
      }
      const onShelf = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0];
      const first = paths
        .map((other) => other.units.find((unit) => unit.key === FORM_ATTR)?.value ?? "")
        .find((form) => form !== "");
      return onShelf ?? first ?? null;
    };

    // cdt2, then the rows it makes. A product blank on the brand block's level is blank below cdt1.
    const shelfRows = new Map<string, { cdt2: string; members: Member[] }>();
    for (const path of paths) {
      const index = rowAttr
        ? path.units.findIndex((unit) => unit.key === rowAttr && unit.value !== "")
        : -1;
      const line = lineOf.get(path.skuId);
      const placedForm = byForm && index < 0 && !line ? formOnShelf(path) : null;
      const value = line ?? (index >= 0 ? path.units[index].value : (placedForm ?? ""));
      if (value === "" || rowAttr === null) {
        out.set(path.skuId, ["", ""]);
        continue;
      }
      if (placedForm)
        notes.push(`${path.skuId} has no form; placed in "${placedForm}" as the base shows`);
      // Spent: the brand block's row level. Levels above it stay open: they may still read
      // along the shelf inside the rows.
      const spent = new Set([rowAttr]);
      const node = [head, value].join(PATH_SEPARATOR);
      const shelfRow = shelfRows.get(node) ?? { cdt2: value, members: [] };
      shelfRow.members.push(memberOf(path, spent));
      shelfRows.set(node, shelfRow);
    }

    // Brand > Form > Size tier > Flavour: a form row the base stacks by size (ARM & HAMMER's
    // liquids on base 210: Small on shelf 2, Medium on 3, Large on 4; Downy's liquids on fabric
    // care 168, each scent's Small beside its Medium on one shelf, Large below) ships as its size
    // rows, "Liquid : Small" to "Liquid : Large", in the form's place, and they are one size
    // group: the store's shelf count decides which of them share a shelf, never the base's. A
    // line row and a form whose sizes are not stacked keep their row.
    const formRows = new Map(shelfRows);
    const sizeRowsOf = new Map<string, string[]>();
    const lineNamesHere = new Set(lineOf.values());
    if (rowChoice?.orient === "vertical" && byForm && rowAttr !== null) {
      shelfRows.clear();
      for (const [node, shelfRow] of formRows) {
        if (lineNamesHere.has(shelfRow.cdt2) || !stackedBySize(itemsOf(shelfRow.members))) {
          shelfRows.set(node, shelfRow);
          continue;
        }
        const bySize = new Map<string, Member[]>();
        for (const member of shelfRow.members) {
          const size = valueAt(member, SIZE_TIER_ATTR);
          bySize.set(size, [...(bySize.get(size) ?? []), member]);
        }
        const names: string[] = [];
        const spent = new Set([rowAttr, SIZE_TIER_ATTR]);
        for (const size of orderedGroups(bySize, true, itemOf, (placement) => placement.shelf)) {
          const name = [shelfRow.cdt2, size].map(sanitizeCdtValue).join(COMPOUND_JOIN);
          names.push(name);
          shelfRows.set([head, name].join(PATH_SEPARATOR), {
            cdt2: name,
            members: (bySize.get(size) ?? []).map((member) => memberOf(member.path, spent)),
          });
        }
        sizeRowsOf.set(shelfRow.cdt2, names);
        notes.push(
          `the base stacks "${shelfRow.cdt2}" by size, so it ships as the size rows ${names.map((name) => `"${name}"`).join(", ")}, which may share shelves on a store`,
        );
      }
    }

    // cdt3 along the shelf inside the rows.
    const inShelfRows = [...shelfRows.values()].flatMap((shelfRow) => shelfRow.members);
    const shelfRowMembers = [...shelfRows.values()].map((shelfRow) => shelfRow.members);
    const splitsAShelfRow = (key: string) =>
      shelfRowMembers.some((members) => splitsInto(members, key) >= 2);
    const scopes = [...shelfRows].flatMap(([node, shelfRow]) =>
      scopesOf(shelfRow.members, itemOf, node.split(PATH_SEPARATOR)),
    );
    const scored3 = keyOrder(inShelfRows).filter(
      (key) => singleAttr(key) && levelAllowed(key) && key !== rowAttr && splitsAShelfRow(key),
    );
    const cdt3Scores = readLevels(
      scored3,
      itemsOf(inShelfRows),
      scopes,
      shelfRowMembers,
      valueOrder,
    );
    const cdt3Groups = Object.fromEntries(
      scored3.map((key) => [key, levelGroupsOf(inShelfRows, key)]),
    );
    const acrossScores = (exclude?: string) =>
      Object.fromEntries(
        Object.entries(cdt3Scores)
          .filter(([key]) => key !== exclude)
          .map(([key, score]) => [key, score.across]),
      );
    // In a form-first brand block the sub-brand reads inside each form where the base lays the
    // forms out by it; else the size tier reads inside each form where it splits it. Under a
    // sub-brand cdt2 the size tier reads inside each sub-brand where it splits it and the base
    // does not contradict it. A row the chosen level does not split takes the level the base
    // reads best along the shelf.
    const formSubBrand =
      byForm && splitsAShelfRow(SUB_BRAND_ATTR)
        ? subBrandLayoutOf(itemsOf(inShelfRows), scopes)
        : null;
    const innerTier = formSubBrand
      ? SUB_BRAND_ATTR
      : (byForm && splitsAShelfRow(SIZE_TIER_ATTR)) ||
          (bySubBrand &&
            splitsAShelfRow(SIZE_TIER_ATTR) &&
            sizeTierReadsAsRows(itemsOf(inShelfRows), scopes))
        ? SIZE_TIER_ATTR
        : null;
    const along = chooseLevel(
      acrossScores(innerTier ?? undefined),
      cdt3Groups,
      scored3,
      oneRow ? [...GROUPING, ...LAST_RESORT] : GROUPING,
    );
    const cdt3: TierChoice | null = formSubBrand
      ? { attr: SUB_BRAND_ATTR, source: "sub-brand", orient: formSubBrand }
      : innerTier === SIZE_TIER_ATTR
        ? { attr: SIZE_TIER_ATTR, source: "size tier", orient: "vertical" }
        : along
          ? { attr: along, source: "base", orient: "horizontal" }
          : null;
    const cdt2: TierChoice | null =
      rowChoice ??
      (oneRow && cdt3 ? { attr: oneRow, source: "one row", orient: "vertical" } : null);
    if (cdt2 === null) {
      shelfRows.clear();
      formRows.clear();
      for (const path of paths) out.set(path.skuId, ["", ""]);
    }

    // A line row stands where the base stands it: the first taken outermost, top or bottom.
    const lineNames = new Set(lineOf.values());
    const edgeNames = (side: RowLine["side"]) =>
      takenLines.filter((line) => line.side === side).map((line) => sanitizeCdtValue(line.name));
    const rowOrder = [
      ...edgeNames("top"),
      ...orderedGroups(
        new Map(
          [...formRows.values()]
            .filter((shelfRow) => !lineNames.has(shelfRow.cdt2))
            .map((shelfRow) => [shelfRow.cdt2, shelfRow.members]),
        ),
        rowAttr === SIZE_TIER_ATTR && cdt2?.orient !== "horizontal",
        itemOf,
        cdt2?.orient === "horizontal" ? xOf : (placement) => placement.shelf,
      ).flatMap((row) => sizeRowsOf.get(row) ?? [row]),
      ...edgeNames("bottom").reverse(),
    ];
    // Brand > Size tier > Flavour: the size tier is the brand block's own rows and the base
    // stacks them (Tide : Liquid on base 210; Tide's powders, Small beside Medium on top, Large
    // below): those rows are one size group. A line row is not in it.
    const sizeGroups = [...sizeRowsOf.values()].filter((names) => names.length >= 2);
    if (rowAttr === SIZE_TIER_ATTR && cdt2?.orient === "vertical") {
      const sizeRows = rowOrder.filter((row) => !lineNames.has(row));
      const members = [...shelfRows.values()]
        .filter((shelfRow) => !lineNames.has(shelfRow.cdt2))
        .flatMap((shelfRow) => shelfRow.members);
      if (sizeRows.length >= 2 && stackedBySize(itemsOf(members))) sizeGroups.push(sizeRows);
    }

    // cdt3 per row: the values each product takes.
    const cdt3Of = new Map<string, string>();
    const rowAttrOf = new Map<string, string | null>();
    for (const { cdt2: row, members } of shelfRows.values()) {
      // A form the sub-brand does not split still reads its size tier where that splits it.
      const attr = innerTier
        ? splitsInto(members, innerTier) >= 2
          ? innerTier
          : byForm && splitsInto(members, SIZE_TIER_ATTR) >= 2
            ? SIZE_TIER_ATTR
            : along
        : (cdt3?.attr ?? null);
      rowAttrOf.set(row, attr);
      for (const member of members)
        cdt3Of.set(member.path.skuId, attr ? valueAt(member, attr) : "");
    }
    // A brand block with size groups orders cdt3 once for the whole block, read off the base row
    // by row, so a flavour stands in the same place in every row (`blockOrder`).
    const cdt3Order =
      sizeGroups.length > 0
        ? blockOrder(
            [...shelfRows.values()].map(({ cdt2: row, members }) => ({
              row,
              members: members.filter((member) => cdt3Of.get(member.path.skuId) !== ""),
            })),
            (member) => cdt3Of.get(member.path.skuId) ?? "",
            itemOf,
            xOf,
            sizeGroups.flat(),
            new Set(
              [...shelfRows.values()]
                .filter(({ cdt2: row }) => rowAttrOf.get(row) === SIZE_TIER_ATTR)
                .flatMap(({ members }) => members.map((m) => cdt3Of.get(m.path.skuId) ?? "")),
            ),
          )
        : null;
    for (const { cdt2: row, members } of shelfRows.values()) {
      const attr = rowAttrOf.get(row) ?? null;
      const groups = new Map<string, Member[]>();
      for (const member of members) {
        const value = cdt3Of.get(member.path.skuId) ?? "";
        if (value !== "") groups.set(value, [...(groups.get(value) ?? []), member]);
      }
      // Without size groups each row orders its own cdt3, read the same way off that row alone;
      // a size tier the row is read by keeps Small to Family, as the base stacks it.
      const groupOrder =
        cdt3Order ??
        (attr === SIZE_TIER_ATTR
          ? orderedGroups(groups, true, itemOf, xOf)
          : blockOrder(
              [
                {
                  row,
                  members: members.filter((member) =>
                    groups.has(cdt3Of.get(member.path.skuId) ?? ""),
                  ),
                },
              ],
              (member) => cdt3Of.get(member.path.skuId) ?? "",
              itemOf,
              xOf,
              [],
            ));
      const rowAt = rowOrder.indexOf(row);
      for (const member of members) {
        const value = cdt3Of.get(member.path.skuId) ?? "";
        out.set(member.path.skuId, [row, value]);
        const groupAt = groupOrder.indexOf(value);
        rank.set(member.path.skuId, [
          rowAt < 0 ? Number.POSITIVE_INFINITY : rowAt,
          groupAt < 0 ? Number.POSITIVE_INFINITY : groupAt,
        ]);
      }
    }
    // The size rows' products and flavours; a row read by its size tier has none.
    const sizeRowProducts = cdt3Order
      ? [...shelfRows.values()]
          .filter(({ cdt2: row }) => sizeGroups.some((group) => group.includes(row)))
          .flatMap(({ cdt2: row, members }) =>
            members.flatMap((member) => {
              const item = itemOf.get(member.path.skuId);
              const flavour =
                rowAttrOf.get(row) === SIZE_TIER_ATTR ? "" : (cdt3Of.get(member.path.skuId) ?? "");
              return item ? [{ item, flavour }] : [];
            }),
          )
      : [];
    const sizeLayout = cdt3Order ? sizeLayoutOf(sizeRowProducts, xOf) : null;
    // Horizontal blocking (read off the base, or the merchant's answer): sub-brand rows, the size
    // tiers inside each row. An answer wins even where this plan reads the block clearly: the
    // question is asked from the as-built planogram, which may read it neither way.
    const answer = sizeLayout ? layoutAnswers[head] : undefined;
    const horizontal = sizeLayout !== null && (answer ?? sizeLayout.layout) === "horizontal";
    const subBrandRows =
      horizontal && sizeLayout
        ? subBrandRowsOf(
            sizeGroups,
            [...shelfRows.values()],
            rowOrder,
            sizeLayout.subBrands,
            (member) => {
              const item = itemOf.get(member.path.skuId);
              const flavour = cdt3Of.get(member.path.skuId) ?? "";
              return item ? subBrandOf(item, flavour) : flavour;
            },
            (member) =>
              (itemOf.get(member.path.skuId)?.placements ?? []).map(
                (placement) => `${placement.run}:${placement.shelf}`,
              ),
            rank,
          )
        : null;
    if (subBrandRows) {
      for (const [skuId, labels] of subBrandRows.labels) out.set(skuId, labels);
      for (const [skuId, at] of subBrandRows.rank) rank.set(skuId, at);
    } else if (horizontal) {
      notes.push(
        "horizontal blocking ships its size tier rows as they are: sub-brand bands need one size group whose every product names a sub-brand, none named like another row",
      );
    }
    readings.push({
      column: head,
      skus: paths.length,
      shelves,
      cdt2Scores,
      cdt3Scores,
      cdt2Groups,
      cdt3Groups,
      cdt2,
      cdt3,
      rowOrder: subBrandRows?.rowOrder ?? rowOrder,
      notes,
      ...(cdt3Order
        ? {
            sizeTiers: {
              groups: sizeGroups,
              cdt3Order,
              within: withinOrderOf(sizeRowProducts, xOf),
              // An answered block ships the merchant's answer (`withBrandBlockDirections`); an
              // unclear one waits for it at Finalize (the panel's gate).
              layout:
                sizeLayout && sizeLayout.layout !== "unclear" ? sizeLayout.layout : "sizeTiers",
            },
            ...(sizeLayout?.layout === "unclear"
              ? {
                  sizeLayoutQuestion: {
                    subBrands: sizeLayout.subBrands,
                    sizesRunUp: sizeLayout.sizesRunUp,
                  },
                }
              : {}),
          }
        : {}),
      ...(replication
        ? {
            replicates: replication.replicates,
            lines: replication.lines,
            unexplained: replication.unexplained,
          }
        : {}),
    });
  }
  return { tiers: out, rank, readings };
};

/** Horizontal blocking as sub-brand rows (Wendy, 2026-10-23: "it really should have come up as
 *  sub-brand and then size tier"). The products of the block's one size group take their
 *  sub-brand (`subBrandOf`) as cdt2 and their size tier row as cdt3: the sub-brand rows stand
 *  top to bottom in the base's order (`subBrands`, the first shelf each stands on, then left to
 *  right; a sub-brand with no product on the base after them), and the size tiers inside each
 *  sub-brand row keep the group's order, Small to Large. The sub-brand rows take the size rows'
 *  place among the block's rows; a row taken out as a line keeps its labels and its place. Null,
 *  and the block ships its size tier rows as they are, where there is not exactly one size group,
 *  a product of it names no sub-brand, or a sub-brand is named like another row of the block.
 *
 *  The top or bottom size tier the base stands across the sub-brands stays one row of its own
 *  (`acrossSubBrands`), above or below the sub-brand rows: its own shelves, no other size tier on
 *  them, and two or more sub-brands side by side on one of them. Base 210's Tide : Unit dose: the
 *  Small packs, all soft, on shelves 1-2 (plus Downy beside Original on shelf 1, Power PODS with
 *  Downy on shelf 2), the Medium packs as sub-brand rows below (Original on shelf 3, Power PODS
 *  on 4-5). Split into sub-brand rows, a store planogram stood each soft pack at the left end of
 *  its sub-brand's row. */
const subBrandRowsOf = (
  sizeGroups: readonly (readonly string[])[],
  shelfRows: readonly { cdt2: string; members: readonly Member[] }[],
  rowOrder: readonly string[],
  subBrands: readonly string[],
  subBrandOfMember: (member: Member) => string,
  shelvesOfMember: (member: Member) => readonly string[],
  rank: ReadonlyMap<string, readonly [number, number]>,
): {
  labels: Map<string, [string, string]>;
  rank: Map<string, readonly [number, number]>;
  rowOrder: string[];
} | null => {
  if (sizeGroups.length !== 1) return null;
  const tiers = sizeGroups[0];
  const membersOf = (tier: string) =>
    shelfRows.filter(({ cdt2: row }) => row === tier).flatMap(({ members }) => members);
  /** The base stands this size tier across the sub-brands: on shelves of its own, two or more
   *  sub-brands side by side on one of them. */
  const acrossSubBrands = (tier: string): boolean => {
    const own = new Map<string, Set<string>>();
    for (const member of membersOf(tier))
      for (const shelf of shelvesOfMember(member))
        own.set(shelf, (own.get(shelf) ?? new Set<string>()).add(subBrandOfMember(member)));
    const shared = tiers.some(
      (other) =>
        other !== tier &&
        membersOf(other).some((member) => shelvesOfMember(member).some((s) => own.has(s))),
    );
    return !shared && [...own.values()].some((onShelf) => onShelf.size >= 2);
  };
  const topRow = tiers.length >= 2 && acrossSubBrands(tiers[0]) ? [tiers[0]] : [];
  const bottomRow =
    tiers.length >= 2 && acrossSubBrands(tiers[tiers.length - 1]) ? [tiers[tiers.length - 1]] : [];
  const ownRows = [...topRow, ...bottomRow];
  if (ownRows.length === tiers.length) return null;
  const subBrandRowOf = new Map<string, string>();
  for (const { cdt2: row, members } of shelfRows)
    if (tiers.includes(row) && !ownRows.includes(row))
      for (const member of members)
        subBrandRowOf.set(member.path.skuId, sanitizeCdtValue(subBrandOfMember(member)));
  const named = new Set(subBrandRowOf.values());
  if (named.has("")) return null;
  const onBase = [...new Set(subBrands.map(sanitizeCdtValue))].filter((subBrand) =>
    named.has(subBrand),
  );
  const subBrandRows = [
    ...onBase,
    ...[...named].filter((subBrand) => !onBase.includes(subBrand)).sort(),
  ];
  const others = rowOrder.filter((row) => !tiers.includes(row));
  if (subBrandRows.some((subBrand) => others.includes(subBrand) || ownRows.includes(subBrand)))
    return null;
  const first = Math.max(
    rowOrder.findIndex((row) => tiers.includes(row)),
    0,
  );
  const order = [
    ...rowOrder.slice(0, first).filter((row) => !tiers.includes(row)),
    ...topRow,
    ...subBrandRows,
    ...bottomRow,
    ...rowOrder.slice(first).filter((row) => !tiers.includes(row)),
  ];
  const labels = new Map<string, [string, string]>();
  const ranks = new Map<string, readonly [number, number]>();
  for (const { cdt2: row, members } of shelfRows)
    for (const member of members) {
      const skuId = member.path.skuId;
      const subBrand = subBrandRowOf.get(skuId);
      if (ownRows.includes(row)) {
        // A size tier standing across the sub-brands: one row, its own size tier as its cdt3.
        labels.set(skuId, [row, row]);
        ranks.set(skuId, [order.indexOf(row), 0]);
      } else if (subBrand !== undefined) {
        labels.set(skuId, [subBrand, row]);
        ranks.set(skuId, [order.indexOf(subBrand), tiers.indexOf(row)]);
      } else {
        const at = order.indexOf(row);
        ranks.set(skuId, [
          at < 0 ? Number.POSITIVE_INFINITY : at,
          rank.get(skuId)?.[1] ?? Number.POSITIVE_INFINITY,
        ]);
      }
    }
  return { labels, rank: ranks, rowOrder: order };
};

const COUNT = /^\d+(\.\d+)?$/;

/** DEV-3531: the cdt2 block keeps its slots. */
const countsAscending = (
  walk: readonly string[],
  itemValues: Readonly<Record<string, readonly string[]>>,
): string[] => {
  const scopeOf = (skuId: string) => (itemValues[skuId] ?? []).slice(0, 3).join(PATH_SEPARATOR);
  const countOf = (skuId: string) => itemValues[skuId]?.[3] ?? "";
  const slots = new Map<string, number[]>();
  walk.forEach((skuId, index) => {
    const scope = scopeOf(skuId);
    slots.set(scope, [...(slots.get(scope) ?? []), index]);
  });
  const out = [...walk];
  for (const indexes of slots.values()) {
    const members = indexes.map((index) => walk[index]);
    if (!members.every((skuId) => COUNT.test(countOf(skuId)))) continue;
    const at = new Map(members.map((skuId, index) => [skuId, index]));
    members.sort((a, b) => Number(countOf(a)) - Number(countOf(b)) || at.get(a)! - at.get(b)!);
    indexes.forEach((index, i) => (out[index] = members[i]));
  }
  return out;
};

/** The walk the shipped priorities are numbered against. Inside each brand block read off the base,
 *  products are re-read row by row (cdt2 top to bottom: Small to Family for the size tier,
 *  else as the base stacks them), then group by group (cdt3 left to right as the base lays
 *  them; the size tier Small to Family; a count small to large), each keeping its walk order;
 *  the brand block keeps its slots in the walk, so cdt0 and cdt1 priorities do not move. */
const priorityWalkOf = (
  walk: readonly string[],
  itemValues: Readonly<Record<string, readonly string[]>>,
  rank: ReadonlyMap<string, readonly [number, number]>,
  sortCounts: boolean,
): string[] => {
  const base = baseWalkOf(walk, itemValues, rank);
  return sortCounts ? countsAscending(base, itemValues) : base;
};

const baseWalkOf = (
  walk: readonly string[],
  itemValues: Readonly<Record<string, readonly string[]>>,
  rank: ReadonlyMap<string, readonly [number, number]>,
): string[] => {
  if (rank.size === 0) return [...walk];
  const brandBlockOf = (skuId: string) =>
    (itemValues[skuId] ?? []).slice(0, 2).join(PATH_SEPARATOR);
  const at = new Map(walk.map((skuId, index) => [skuId, index]));
  const slots = new Map<string, number[]>();
  walk.forEach((skuId, index) => {
    const column = brandBlockOf(skuId);
    slots.set(column, [...(slots.get(column) ?? []), index]);
  });
  const out = [...walk];
  const unread = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY] as const;
  for (const indexes of slots.values()) {
    const members = indexes.map((index) => walk[index]);
    members.sort((a, b) => {
      const [rowA, groupA] = rank.get(a) ?? unread;
      const [rowB, groupB] = rank.get(b) ?? unread;
      if (rowA !== rowB) return rowA < rowB ? -1 : 1;
      if (groupA !== groupB) return groupA < groupB ? -1 : 1;
      return (at.get(a) ?? 0) - (at.get(b) ?? 0);
    });
    indexes.forEach((index, i) => (out[index] = members[i]));
  }
  return out;
};

/** Every shipped tier's nodes (cdt1 vertical blocks, cdt2 shelf rows, cdt3 groups) come first
 *  in the same order in `generated` as on the canvas walk. The order of products inside one
 *  cdt3 group is not shipped, so it is not part of the check. Below cdt0 a node is ordered within
 *  its cdt0: a brand carved into a small-pack row over a large-pack row is walked column by
 *  column, crossing from one cdt0 to the other, which is no order the tree ships; the cdt0s' own
 *  order is the first tier's check. */
export const tiersFollowWalk = (
  itemValues: Readonly<Record<string, readonly string[]>>,
  canvasWalk: readonly string[],
  generated: readonly string[],
): boolean => {
  const depth = Math.max(0, ...Object.values(itemValues).map((values) => values.length));
  const firstSeen = (walk: readonly string[], tier: number): Map<string, string> => {
    const cdt0Of = new Map<string, string>();
    for (const skuId of walk) {
      const path = itemValues[skuId] ?? [];
      const node = path.slice(0, tier + 1).join(PATH_SEPARATOR);
      if (!cdt0Of.has(node)) cdt0Of.set(node, path[0] ?? "");
    }
    return cdt0Of;
  };
  const nodesOf = (walk: readonly string[], tier: number): string[] => {
    const cdt0Rank = new Map([...firstSeen(walk, 0).keys()].map((node, index) => [node, index]));
    return [...firstSeen(walk, tier)]
      .map(([node, cdt0], index) => ({ node, index, cdt0: cdt0Rank.get(cdt0) ?? 0 }))
      .sort((a, b) => a.cdt0 - b.cdt0 || a.index - b.index)
      .map((entry) => entry.node);
  };
  for (let tier = 0; tier < depth; tier += 1) {
    const canvas = nodesOf(canvasWalk, tier);
    const shipped = nodesOf(generated, tier);
    if (canvas.length !== shipped.length || canvas.some((node, i) => shipped[i] !== node))
      return false;
  }
  return true;
};

/** The brand blocks whose cdt2 stands side by side, and the size groups of brand blocks read
 *  Brand > [Form >] Size tier > Flavour (`sizeTiers`), named on the __cdt2 level by the release's
 *  own cdt0 and cdt1. Side by side: those the base lays out by sub-brand side by side (`readings`), and those
 *  whose direction the merchant set (`StudioDraft.cdt2Directions`), which wins. The level's
 *  `orient` answers every other brand block: stacked. A size-tier block the merchant answered
 *  ships that answer (`answers`, `StudioDraft.sizeLayoutAnswers`), even where this plan no
 *  longer asks: the question comes from the as-built planogram. */
const withBrandBlockDirections = (
  levels: readonly CollapsedLevel[],
  overrides: Readonly<Record<string, Orientation>> | undefined,
  shipped: ReadonlySet<string>,
  readings: readonly BrandBlockTierReading[],
  answers: Readonly<Record<string, SizeLayout>> | undefined,
): CollapsedLevel[] => {
  const fromBase = Object.fromEntries(
    readings.flatMap((reading) =>
      reading.cdt2?.orient === "horizontal" ? [[reading.column, reading.cdt2.orient]] : [],
    ),
  ) as Record<string, Orientation>;
  const columnOrients = Object.entries({ ...fromBase, ...overrides })
    .filter(([column]) => shipped.has(column))
    .map(([column, orient]) => {
      const [cdt0, cdt1] = column.split(PATH_SEPARATOR);
      return { cdt0, cdt1, orient };
    });
  // Brand blocks read Brand > [Form >] Size tier > Flavour: their size groups and one cdt3
  // order for the block. A block the merchant stands side by side has no shelf rows to merge.
  const sizeTiers = readings.flatMap((reading) => {
    if (!reading.sizeTiers || !shipped.has(reading.column)) return [];
    if (overrides?.[reading.column] === "horizontal") return [];
    const [cdt0, cdt1] = reading.column.split(PATH_SEPARATOR);
    const answer = answers?.[reading.column];
    return [{ cdt0, cdt1, ...reading.sizeTiers, ...(answer ? { layout: answer } : {}) }];
  });
  return levels.map((level, tier) =>
    tier === 2
      ? {
          ...level,
          ...(columnOrients.length > 0 ? { columnOrients } : {}),
          ...(sizeTiers.length > 0 ? { sizeTiers } : {}),
        }
      : level,
  );
};

/** The ship draft with the category profile's declared levels stamped on. */
export const withProfileLevels = (
  draft: StudioDraft,
  levels: readonly string[] | undefined,
): StudioDraft => (levels && levels.length > 0 ? { ...draft, profileLevels: [...levels] } : draft);

export interface CollapseResult {
  /** Preserve tier decisions so the panel can explain re-nesting and unresolved ambiguity. */
  plan: CdtLevelPlan;
  contract: CollapsedContract;
  collapsedItems: StudioItem[];
  collapsedDraft: StudioDraft;
  verification: { identical: boolean; orderPreserved: boolean; diffs: LayoutDiff[] };
  generated: GeneratedLayout;
  /** The walk the shipped priorities are numbered against (`compoundPriorities`): the
   *  contract walk, with each brand block read off the base re-read row by row, then group by
   *  group, as the base shows them. */
  priorityWalk: readonly string[];
  /** What the base pog said about each brand block's cdt2 and cdt3, with every candidate's
   *  strength. */
  tierReadings: readonly BrandBlockTierReading[];
}

/** Where a product stands, x said as whole inches (raw units / 100). */
const describeSegment = (layout: GeneratedLayout, skuId: string): string => {
  const placement = layout.placements[skuId];
  if (!placement) return "missing";
  return placement.segments
    .map((s) => `r${s.run}s${s.shelf}@${wholeInches(s.xCm)}×${s.hFacings}`)
    .join(" ");
};

export const collapse4 = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  deepLayout: GeneratedLayout,
  contractWalkOrder: readonly string[] = buildLayout(items, draft, runs).walkOrder,
  strategy: CompressionStrategy = {},
): CollapseResult => {
  const planned = planCompression(items, draft, runs, strategy);
  // Tree mode: a cdt1 child wide enough to stand as its own vertical block joins cdt1
  // ("Tide : Pods"), but only while the parent's columns stay two facings wide.
  // A horizontal wall has no columns to lift a child into.
  const wide =
    strategy.mode !== "measured" && planned.blocking !== "horizontal"
      ? gateWideChildren(
          planned,
          items,
          draft,
          runs,
          selectiveCdt1(items, draft, runs, planned, contractWalkOrder),
        )
      : { joined: new Map<string, WideJoin>(), notes: [] as string[] };
  const plan: CdtLevelPlan =
    wide.notes.length > 0
      ? { ...planned, warnings: [...planned.warnings, ...wide.notes] }
      : planned;
  const groups: LevelGroup[] = plan.levels.map((level) => ({
    levels: level.levels,
    orient: level.orient,
    role: level.role,
  }));

  const levels: CollapsedLevel[] = groups.map((group, index) => ({
    attr: `__cdt${index}`,
    label: group.levels.map((level) => level.label).join(" + "),
    sourceAttrs: group.levels.map((level) => level.attr),
    orient: group.orient,
  }));
  const tierAttrsForBranch = branchTierAttrs(items, draft, plan);

  const drafted = items.map((item) => {
    const attrs = effectiveAttrs(item, draft);
    const compound: Record<string, string | null> = {};
    const planned = tierAttrsForBranch(attrs);
    const join = wide.joined.get(item.skuId);
    const consumed = join ? withoutJoinedLevel(planned, join.attr) : planned;
    let tierAttrs = skipBlankTiers(attrs, consumed, levels.length);
    // A product blank at its planned cdt1 has had a lower level lifted there: it joins
    // nothing, so it keeps the child's level on its path.
    const joins = join !== undefined && tierAttrs[1] === consumed[1];
    if (join && !joins) tierAttrs = skipBlankTiers(attrs, planned, levels.length);
    levels.forEach((level, tier) => {
      compound[level.attr] = (tierAttrs[tier] ?? [])
        .map((attr) => sanitizeCdtValue(attrs[attr]))
        .join(COMPOUND_JOIN);
    });
    if (join && joins)
      compound.__cdt1 = `${compound.__cdt1}${COMPOUND_JOIN}${sanitizeCdtValue(join.value)}`;
    const path: PathDraft = {
      skuId: item.skuId,
      head: [compound.__cdt0 ?? "", compound.__cdt1 ?? ""].join(PATH_SEPARATOR),
      units: unitsBelowCdt1(attrs, tierAttrs),
    };
    return { item, compound, path };
  });
  // Decided per node over the walk-sorted products, so first appearance follows the walk.
  const walkRank = new Map(contractWalkOrder.map((skuId, index) => [skuId, index]));
  // The tiers are read off the base only where Studio reads the base as vertically blocked
  // and the plan is the tree's own. A horizontally blocked planogram and a measured plan already
  // read the planogram their own way: there the release is exactly what it was before (no
  // constant skip, no tier pick), so this whole pass stays off.
  const readBase = strategy.mode !== "measured" && plan.blocking !== "horizontal";
  const informative =
    levels.length === 4 && readBase
      ? informativeTiers(
          drafted
            .map((entry) => entry.path)
            .sort((a, b) => (walkRank.get(a.skuId) ?? 0) - (walkRank.get(b.skuId) ?? 0)),
          // Read on the base: each product's own attributes where it stands on the base pog.
          new Map(
            items.map((item) => [item.skuId, { ...item, attrs: effectiveAttrs(item, draft) }]),
          ),
          runs,
          draft.valueOrder,
          draft.profileLevels,
          draft.quantityAttribute,
          draft.spine.map((level) => level.attr),
          new Set(draft.appliedFixFlagIds),
          draft.sizeLayoutAnswers,
        )
      : {
          tiers: new Map<string, [string, string]>(),
          rank: new Map<string, readonly [number, number]>(),
          readings: [] as BrandBlockTierReading[],
        };

  const collapsedItems: StudioItem[] = drafted.map(({ item, compound, path }) => {
    const tiers = informative.tiers.get(path.skuId);
    if (tiers) [compound.__cdt2, compound.__cdt3] = tiers;
    return {
      ...item,
      attrs: compound,
      placements: layoutPlacementsOf(item, deepLayout),
    };
  });

  // Walk order comes from the persisted draft so a release is reproducible. Inside a brand
  // block read off the base, cdt2 rows come top to bottom and cdt3 groups left to right as the
  // base shows them, size tiers Small to Family (see `priorityWalkOf`).
  const shippedValues = Object.fromEntries(
    collapsedItems.map((item) => [item.skuId, levels.map((level) => item.attrs[level.attr] ?? "")]),
  );
  // Counts read small to large only where the tiers are read off the base (tree mode, vertical
  // wall): a measured plan and a horizontal wall ship exactly what they did before.
  const priorityWalk = priorityWalkOf(
    contractWalkOrder,
    shippedValues,
    informative.rank,
    levels.length === 4 && readBase,
  );
  const rank = new Map(priorityWalk.map((skuId, index) => [skuId, index]));
  const walkSorted = [...collapsedItems].sort(
    (a, b) => (rank.get(a.skuId) ?? 0) - (rank.get(b.skuId) ?? 0),
  );
  const valueOrder: Record<string, string[]> = {};
  for (const level of levels) {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const item of walkSorted) {
      const value = item.attrs[level.attr] ?? "";
      if (!seen.has(value)) {
        seen.add(value);
        order.push(value);
      }
    }
    valueOrder[level.attr] = order;
  }

  const spine2: SpineLevel[] = levels.map((level) => ({
    attr: level.attr,
    label: level.label,
    orient: level.orient,
  }));
  const annotations = {
    scopedValueOrder: {} as Record<string, string[]>,
    bandOverrides: {} as Record<string, number[]>,
    orientOverrides: {} as Record<string, Orientation>,
  };
  annotateContract(collapsedItems, spine2, runs, annotations);

  // Compound values are not globally unique; order per parent like the generator.
  const scopedFromWalk: Record<string, string[]> = {};
  const recordScopedOrders = (
    scopeItems: readonly StudioItem[],
    levelIndex: number,
    path: readonly string[],
  ) => {
    if (levelIndex >= spine2.length || scopeItems.length <= 1) return;
    const attr = spine2[levelIndex].attr;
    const groups = new Map<string, StudioItem[]>();
    for (const item of scopeItems) {
      const value = item.attrs[attr] ?? "";
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    if (groups.size < 2) {
      recordScopedOrders(scopeItems, levelIndex + 1, path);
      return;
    }
    const ordered = [...groups.entries()].sort(
      (a, b) =>
        Math.min(...a[1].map((item) => rank.get(item.skuId) ?? 0)) -
        Math.min(...b[1].map((item) => rank.get(item.skuId) ?? 0)),
    );
    scopedFromWalk[path.join(PATH_SEPARATOR)] = ordered.map(([value]) => value);
    for (const [value, group] of ordered) {
      recordScopedOrders(group, levelIndex + 1, [...path, value]);
    }
  };
  recordScopedOrders(collapsedItems, 0, []);

  const collapsedDraft: StudioDraft = {
    spine: spine2,
    valueOrder,
    scopedValueOrder: scopedFromWalk,
    bandOverrides: annotations.bandOverrides,
    orientOverrides: annotations.orientOverrides,
    shelfHints: Object.fromEntries(
      collapsedItems.map((item) => [
        item.skuId,
        item.placements.map((placement) => ({
          shelf: placement.shelf,
          hFacings: placement.hFacings,
        })),
      ]),
    ),
    attrOverrides: {},
    restrictions: draft.restrictions,
    dismissedFlagIds: [],
    appliedFixFlagIds: [],
  };

  const generated = buildLayout(collapsedItems, collapsedDraft, runs);
  const diffs: LayoutDiff[] = [];
  for (const item of items) {
    const before = describeSegment(deepLayout, item.skuId);
    const after = describeSegment(generated, item.skuId);
    if (before !== after) diffs.push({ skuId: item.skuId, before, after });
  }
  const orderPreserved = tiersFollowWalk(
    Object.fromEntries(
      collapsedItems.map((item) => [
        item.skuId,
        levels.map((level) => item.attrs[level.attr] ?? ""),
      ]),
    ),
    deepLayout.walkOrder,
    generated.walkOrder,
  );

  const itemValues: Record<string, string[]> = {};
  for (const item of collapsedItems) {
    itemValues[item.skuId] = levels.map((level) => item.attrs[level.attr] ?? "");
  }
  // Where the tiers below cdt1 were read off the base, cdt2 and cdt3 carry one level per brand
  // block: the release names the levels they ship, not the plan's.
  const labelOf = (attr: string) => draft.spine.find((level) => level.attr === attr)?.label ?? attr;
  // Only where a brand block takes its sub-brand below cdt1, which moves the size tier down to
  // cdt3; elsewhere the plan's levels ship, as before.
  const bySubBrand = informative.readings.some(
    (reading) => reading.cdt2?.source === "sub-brand" || reading.cdt3?.source === "sub-brand",
  );
  const shippedLevels = levels.map((level, tier) => {
    if (tier < 2 || !bySubBrand) return level;
    const attrs = [
      ...new Set(
        informative.readings.flatMap((reading) => {
          const choice = tier === 2 ? reading.cdt2 : reading.cdt3;
          return choice ? [choice.attr] : [];
        }),
      ),
    ];
    if (attrs.length === 0) return level;
    return { ...level, label: attrs.map(labelOf).join(" + "), sourceAttrs: attrs };
  });

  return {
    plan,
    contract: {
      levels: withBrandBlockDirections(
        shippedLevels,
        draft.cdt2Directions,
        new Set(Object.values(itemValues).map((values) => values.slice(0, 2).join(PATH_SEPARATOR))),
        informative.readings,
        draft.sizeLayoutAnswers,
      ),
      valueOrder,
      itemValues,
    },
    collapsedItems,
    collapsedDraft,
    verification: { identical: diffs.length === 0, orderPreserved, diffs },
    generated,
    priorityWalk,
    tierReadings: informative.readings,
  };
};

/** Priority of each compound value. CDT1 numbers one global gap-free sequence over the
 * (cdt0, cdt1) PAIRS, zone-major (cdt0s in walk order, each cdt0's pairs a contiguous block);
 * deeper levels number 1..n under each parent, except cdt3 in a brand block with size groups,
 * numbered once for the block (`sizeTiers`; a horizontal block's sub-brand rows number their
 * size tiers 1..n each). Keying by pair, not by bare value, keeps a value
 * that ranges in two cdt0s at each stratum's own wall position. */
export const compoundPriorities = (
  contract: CollapsedContract,
  walkOrder: readonly string[],
): Map<string, number> => {
  const rank = new Map(walkOrder.map((skuId, index) => [skuId, index]));
  const sorted = Object.entries(contract.itemValues).sort(
    (a, b) => (rank.get(a[0]) ?? 0) - (rank.get(b[0]) ?? 0),
  );
  const priorities = new Map<string, number>();
  const counters = new Map<string, number>();
  // A brand block with size groups numbers its cdt3 once for the block (`sizeTiers`): a value
  // takes its place in the block's order in every row. A value the order does not name comes
  // after it, in walk order.
  // A horizontal block ships as sub-brand rows (`subBrandRowsOf`): its cdt3 is the size tier,
  // numbered 1..n inside each sub-brand row in walk order, Small to Large.
  const blockCdt3Order = new Map(
    (contract.levels[2]?.sizeTiers ?? [])
      .filter((entry) => entry.layout !== "horizontal")
      .map((entry) => [[entry.cdt0, entry.cdt1].join(PATH_SEPARATOR), entry.cdt3Order]),
  );
  const unnamedAfter = new Map<string, string[]>();

  const cdt0Order: string[] = [];
  const pairFirstRank = new Map<string, number>();
  sorted.forEach(([, values], index) => {
    const cdt0 = values[0] ?? "";
    if (!cdt0Order.includes(cdt0)) cdt0Order.push(cdt0);
    const pairKey = values.slice(0, CDT1_LEVEL + 1).join(PATH_SEPARATOR);
    if (!pairFirstRank.has(pairKey)) pairFirstRank.set(pairKey, index);
  });
  const cdt1Priorities = new Map<string, number>();
  for (const cdt0 of cdt0Order) {
    const ownPairs = [...pairFirstRank.entries()]
      .filter(([key]) => key.split(PATH_SEPARATOR)[0] === cdt0)
      .sort((a, b) => a[1] - b[1]);
    for (const [key] of ownPairs) cdt1Priorities.set(key, cdt1Priorities.size + 1);
  }

  for (const [, values] of sorted) {
    for (let levelIndex = 0; levelIndex < values.length; levelIndex++) {
      const nodeKey = values.slice(0, levelIndex + 1).join(PATH_SEPARATOR);
      if (priorities.has(nodeKey)) continue;
      if (levelIndex === CDT1_LEVEL) {
        priorities.set(nodeKey, cdt1Priorities.get(nodeKey) ?? 0);
        continue;
      }
      const parentKey = values.slice(0, levelIndex).join(PATH_SEPARATOR);
      const blockKey = values.slice(0, CDT1_LEVEL + 1).join(PATH_SEPARATOR);
      const order = levelIndex === 3 ? blockCdt3Order.get(blockKey) : undefined;
      if (order) {
        const value = values[levelIndex] ?? "";
        const at = order.indexOf(value);
        if (at >= 0) {
          priorities.set(nodeKey, at + 1);
        } else {
          const unnamed = unnamedAfter.get(blockKey) ?? [];
          if (!unnamed.includes(value)) unnamed.push(value);
          unnamedAfter.set(blockKey, unnamed);
          priorities.set(nodeKey, order.length + unnamed.indexOf(value) + 1);
        }
        continue;
      }
      const next = (counters.get(parentKey) ?? 0) + 1;
      counters.set(parentKey, next);
      priorities.set(nodeKey, next);
    }
  }
  return priorities;
};
